import { useCallback, useReducer, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import "./BoardCard.scss";
import { notification, Select, Divider, DatePicker, Button, Tabs, Space } from "antd";
import { SaveOutlined, CopyOutlined } from '@ant-design/icons';
import {
  uuidValidation,
  macValidation,
  imeiValidation,
  lengthValidation,
  typeValidation,
  iccValidation,
} from "../../utils/formValidation.jsx";
import getPlansFormatted from "../../utils/getPlansFormatted.js";
import service from "../../services/Service.js";
import dayjs from 'dayjs';
import { hasPrivilege } from "../../api/token.js";
import { withLocale } from "../../l10n/index.jsx";
import { translate } from "../../l10n/translate";
import { STATE_ACTIVE, STATE_CANCELLED, STATE_INACTIVE, STATE_NOT_APPLICABLE, KICONEX_UUID } from "../../utils/constants.js";
import Loading from "../../utils/loading.jsx";
import withNavigation from '../hooks/withNavigation.jsx';
import { 
  useGetModelsLookupQuery, 
  useGetVersionsLookupQuery, 
  useGetTenantsLookupQuery, 
  useGetRoutersLookupQuery,
  invalidateRoutersLookup
} from '../../services/lookupApi.js';
import M2MSubscriptionFields from '../M2MSubscriptionFields';
import ModemInfo from '../ModemInfo';
import RouterInfo from '../RouterInfo';
import { getRouterLabel } from "../../utils/functions.jsx";

const { Option } = Select;

// ===== CONSTANTES GLOBALES =====
const optionsState = [
  {
    value: '',
    label: translate('Seleccionar opción'),
    disabled: true 
  },
  {
    value: STATE_INACTIVE,
    label: translate('Lista para activación')
  },
  {
    value: STATE_ACTIVE,
    label: translate('Activo')
  },
  {
    value: STATE_CANCELLED,
    label: translate('Cancelado')
  },
  {
    value: STATE_NOT_APPLICABLE,
    label: translate('No procede')
  }
];

// ===== HELPERS GLOBALES =====

const getInitialState = (lang) => ({
  board: {
    ki_id: 0,
    order_id: '',
    name: '',
    tenant_uuid: '',
    final_client: '',
    uuid: '',
    serial_number: '',
    work_order: '',
    hardware_version: 'A20-OLinuXino-LIME2-e16Gs16M',
    physical_id: '',
    date: dayjs().format('DD-MM-YYYY'),
    ki_router_sn: '',
    router_uuid: '',
    notes: '',
    state: '',
    vpn: 0,
    version_uuid: '',
    comercial_info: {
      order_id: '',
      name: '',
      serial_number: '',
      state: 'Stock',
      model: ''
    },
    spare_date: null,
    original_uuid: null
  },
  plan_subscription: {
    ki_subscription_uuid: '',
    ki_subscription_name: '',
    ki_subscription_state: '',
    ki_connection_date: null,
    renewal_date: null,
    renewal_interval: 0,
  },
  m2m_subscription: {
    icc: '',
    state: '',
    hiring_date: null,
    renewal_date: null,
    renewal_interval: 0,
  },
  modem: {
    imei: '',
    modem_type_code: '',
    modem_serial_number: '',
    modem_mac: '',
    modem_device_password: '',
  },
  errors: {
    uuid: '',
    physical_id: '',
    sn: '',
    wo: '',
  },
  ki_plans: {
    name: [],
    uuid: [],
  },
  has_spares: false,
  tenants: [],
  filtered_version_list: [],
  model_uuid: '',
  work_orders: [],
  work_orders_original_length: 0,
  select_loading: true,
  loading: false,
  tenant_name: lang ? lang.handle("Sin organización") : "Sin organización"
});

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_SELECT_LOADING':
      return { ...state, select_loading: action.payload };
    case 'UPDATE_BOARD':
      return { ...state, board: { ...state.board, ...action.payload } };
    case 'UPDATE_BOARD_COMERCIAL':
      return {
        ...state,
        board: {
          ...state.board,
          comercial_info: { ...state.board.comercial_info, ...action.payload }
        }
      };
    case 'UPDATE_PLAN_SUBSCRIPTION':
      return { ...state, plan_subscription: { ...state.plan_subscription, ...action.payload } };
    case 'UPDATE_M2M_SUBSCRIPTION':
      return { ...state, m2m_subscription: { ...state.m2m_subscription, ...action.payload } };
    case 'UPDATE_ERRORS':
      return { ...state, errors: { ...state.errors, ...action.payload } };
    case 'SET_KI_PLANS':
      return { ...state, ki_plans: action.payload };
    case 'SET_HAS_SPARES':
      return { ...state, has_spares: action.payload };
    case 'SET_TENANTS':
      return { ...state, tenants: action.payload };
    case 'SET_FILTERED_VERSION_LIST':
      return { ...state, filtered_version_list: action.payload };
    case 'SET_MODEL_UUID':
      return { ...state, model_uuid: action.payload };
    case 'SET_WORK_ORDERS':
      return { ...state, work_orders: action.payload };
    case 'SET_WORK_ORDERS_ORIGINAL_LENGTH':
      return { ...state, work_orders_original_length: action.payload };
    case 'SET_TENANT_NAME':
      return { ...state, tenant_name: action.payload };
    case 'SET_ALL_DATA':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const EMPTY_ARRAY = [];

function BoardCard({ lang, ident, layer, details, editable, navigate }) {
  // ===== 1. ESTADOS =====
  const [state, dispatch] = useReducer(reducer, getInitialState(lang));

  // ===== 2. SELECTORES =====
  const storeDispatch = useDispatch();
  const { data: models = EMPTY_ARRAY } = useGetModelsLookupQuery();
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();
  const { data: tenants = EMPTY_ARRAY } = useGetTenantsLookupQuery();
  const { data: routers = EMPTY_ARRAY } = useGetRoutersLookupQuery();

  const loadedRouterUuid = useRef('');

  // ===== 3. DERIVADOS =====
  const identProp = useMemo(() => ident ?? { id_stock: null, id_spare: null }, [ident]);
  const spare = useMemo(() => !!(identProp.id_stock && identProp.id_spare) || state.board.spare_date, [identProp, state.board.spare_date]);
  const kiconex_editable = useMemo(() => editable && (
    hasPrivilege('administration_edit') ||
    (layer === 'assign' && hasPrivilege('assign'))
  ), [editable, layer]);

  const planCancelledOrNotApplicable = useMemo(() => 
    state.plan_subscription.ki_subscription_state === STATE_NOT_APPLICABLE 
    || state.plan_subscription.ki_subscription_state === STATE_CANCELLED
    || state.plan_subscription.ki_subscription_state === '',
    [state.plan_subscription.ki_subscription_state]
  );

  const title = useMemo(() => {
    if (spare === true) {
      return lang.handle('Nuevo repuesto') + ":  " + identProp.id_stock + " sustituirá a " + identProp.id_spare;
    } else {
      return lang.handle('Información de kibox') + ":  " + identProp.id_spare;
    }
  }, [spare, identProp, lang]);

  // Calculo del alias para el componente de icc/kite
  const { order_id, serial_number, work_order } = state.board;
  const m2mSuggestedAlias = useMemo(() => {
    const suffix = serial_number ? serial_number : (work_order ? work_order : '');
    return suffix ? `${order_id}-${suffix}` : order_id;
  }, [order_id, serial_number, work_order]);

  // Calculo del tipo de sim en funcion del modelo seleccionado
  const m2mDefaultSimType = useMemo(() => {
    const modelName = models.find(m => m.uuid === state.model_uuid)?.name ?? '';
    return modelName === 'BoxC2' ? '4FF' : '2FF';
  }, [models, state.model_uuid]);

  // Calculo de las opciones disponibles (stock) para el selector de routers
  const routerOptions = useMemo(() => {
    const selected = state.board.router_uuid;

    return routers
      .filter(router => router.state === 'Fabricado' || router.uuid === selected)
      .map(router => ({
        value: router.uuid,
        label: getRouterLabel(router),
      }));
  }, [routers, state.board.router_uuid]);

  // ===== 4. HELPERS =====
  const clearTenant = useCallback(() => {
    dispatch({
      type: 'UPDATE_BOARD',
      payload: { tenant_uuid: '' }
    });
    dispatch({
      type: 'SET_TENANT_NAME',
      payload: lang.handle("Sin organización")
    });
  }, [lang]);

  // ===== 5. FUNCIONES PRINCIPALES =====

  const fetchStaticInitialData = useCallback(async () => {
    dispatch({ type: 'SET_SELECT_LOADING', payload: true });
    try {
      const res = await getPlansFormatted();
      dispatch({
        type: 'SET_KI_PLANS',
        payload: {
          name: res[0],
          uuid: res[1],
        }
      });
    } catch (err) {
      console.error('Error al obtener planes formatados:', err);
    } finally {
      dispatch({ type: 'SET_SELECT_LOADING', payload: false });
    }

    try {
      const woPendingResponse = await service.get('orders/wo', { pending: true });
      if (woPendingResponse && woPendingResponse.data.ok) {
        dispatch({
          type: 'SET_WORK_ORDERS',
          payload: woPendingResponse.data.data.map((item) => ({
            label: item.wo,
            value: item.wo,
            serialNumber: item.serialNumber
          }))
        });
        dispatch({
          type: 'SET_WORK_ORDERS_ORIGINAL_LENGTH',
          payload: woPendingResponse.data.data.length
        });
      }
    } catch (err) {
      console.error('Error al obtener ordenes de trabajo pendientes:', err);
    }
  }, [dispatch]);

  const fetchBoardData = useCallback(async () => {
    if (!identProp.id_spare) {
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    const url = details ? "boards/uuid/" : "boards/";
    try {
      const boardRes = await service.get(url + identProp.id_spare);
      if (boardRes.data.ok === false) {
        notification["error"]({
          message: lang.handle(boardRes.data.message),
        });
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      const data = boardRes.data;

      let dataStock = {};
      if (identProp.id_stock) {
        const stockRes = await service.get(url + identProp.id_stock);
        if (stockRes.data.ok === false) {
          notification["error"]({
            message: lang.handle(stockRes.data.message),
          });
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
        dataStock = stockRes.data;
      }

      const boardData = {
        ki_id: dataStock.ki_id ?? data.ki_id,
        order_id: data.order_id ?? '',
        name: data.name ?? '',
        tenant_uuid: data.tenant_uuid ?? '',
        final_client: data.final_client ?? '',
        uuid: data.uuid,
        serial_number: data.serial_number ?? '',
        work_order: data.work_order ?? '',
        hardware_version: dataStock.hardware_version ?? data.hardware_version,
        physical_id: dataStock.physical_id ?? data.physical_id,
        date: dataStock.date ? dayjs(dataStock.date).format('DD-MM-YYYY') : dayjs(data.date).format('DD-MM-YYYY'), 
        ki_router_sn: data.ki_router_sn ?? '',
        router_uuid: data.router_uuid ?? '',
        notes: data.notes,
        state: layer === 'assign' ? 'Asignado' : data.state,
        vpn: data.vpn ?? 0,
        version_uuid: dataStock.version_uuid ?? data.version_uuid,
        comercial_info: {
          order_id: data.comercial_info?.order_id ?? "",
          name: data.comercial_info?.name ?? "",
          serial_number: data.comercial_info?.serial_number ?? "",
          state: data.comercial_info?.state?.trim() || "Stock",
          model: data.comercial_info?.model ?? ""
        },
        spare_date: data.spare_date ? dayjs(data.spare_date).format('DD-MM-YYYY') : (identProp.id_stock ? dayjs().format('DD-MM-YYYY') : null),
        original_uuid: data.original_uuid ? data.original_uuid : (dataStock.uuid ?? null)
      };

      const planSub = {
        ki_subscription_uuid: data.ki_subscription_uuid ?? '',
        ki_subscription_name: data.ki_subscription_name ?? '',
        ki_subscription_state: data.ki_subscription_state ?? '',
        ki_connection_date: data.ki_connection_date ? dayjs(data.ki_connection_date).format('DD-MM-YYYY') : null,
        renewal_date: data.renewal_date ? dayjs(data.renewal_date).format('DD-MM-YYYY') : null,
        renewal_interval: data.renewal_interval ?? 0,
      };

      const m2mSub = {
        icc: data.icc ?? '',
        state: data.m2m_state ?? '',
        hiring_date: data.m2m_hiring_date ? dayjs(data.m2m_hiring_date).format('DD-MM-YYYY') : null,
        renewal_date: data.m2m_renewal_date ? dayjs(data.m2m_renewal_date).format('DD-MM-YYYY') : null,
        renewal_interval: data.m2m_renewal_interval ?? 0,
      };

      const modemData = {
        imei: data.modem_imei ?? '',
        modem_type_code: data.modem_type_code ?? '',
        modem_serial_number: data.modem_serial_number ?? '',
        modem_mac: data.modem_mac ?? '',
        modem_device_password: data.modem_device_password ?? '',
      }

      loadedRouterUuid.current = boardData.router_uuid;

      dispatch({
        type: 'SET_ALL_DATA',
        payload: {
          board: boardData,
          plan_subscription: planSub,
          m2m_subscription: m2mSub,
          has_spares: !!data.has_spares,
          modem: modemData
        }
      });

    } catch (err) {
      console.error('Error al precargar datos de la placa:', err);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [identProp.id_spare, identProp.id_stock, details, layer, lang, dispatch]);

  // ===== 6. HANDLERS =====
  const onInputChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === 'state' && value === 'Fabricado' && state.has_spares) {
      notification.warning({
        message: lang.handle('No se puede devolver a Stock: la placa tiene repuestos asignados'),
      });
      return;
    }
    const levels = name.split('.');

    let errors = { ...state.errors };
    switch (name) {
      case 'uuid':
        errors.uuid = uuidValidation(value) ? '' : lang.handle('¡UUID erróneo!');
        break;
      case 'physical_id':
        errors.physical_id = macValidation(value) || imeiValidation(value) ? '' : '¡' + lang.handle('Introduzca una MAC o IMEI válidos') + '!';
        break;
      case 'serial_number':
        errors.sn = lengthValidation(value, 14) ? '' : lang.handle('¡Número de serie erróneo!');
        break;
      default:
        break;
    }

    dispatch({ type: 'UPDATE_ERRORS', payload: errors });

    if (levels[0] === 'comercial_info') {
      dispatch({
        type: 'UPDATE_BOARD_COMERCIAL',
        payload: { [levels[1]]: value }
      });
    } else {
      dispatch({
        type: 'UPDATE_BOARD',
        payload: { [name]: value }
      });
    }

    if (name === 'state' && value === 'Fabricado') {
      clearTenant();
    }
  }, [state.has_spares, state.errors, lang, clearTenant]);

  const onChangeDate = useCallback((date, dateString) => {
    dispatch({
      type: 'UPDATE_BOARD',
      payload: { date: dateString }
    });
  }, []);

    const onChangeSelect = useCallback((value, e) => {
    if (e.type === 'ki_subscription_name') {
      const index_plan = state.ki_plans.name.indexOf(value);
      dispatch({
        type: 'UPDATE_PLAN_SUBSCRIPTION',
        payload: {
          ki_subscription_name: value,
          ki_subscription_uuid: state.ki_plans.uuid[index_plan]
        }
      });
    } else if (e.type === "tenant_name") {
      const foundTenant = state.tenants.find((item) => item.name === value);
      const index_tenant = foundTenant ? foundTenant.uuid : '';
      
      dispatch({
        type: 'UPDATE_BOARD',
        payload: { tenant_uuid: index_tenant }
      });

      dispatch({
        type: 'SET_TENANT_NAME',
        payload: value
      });
    } else if (e.type === "model_name") {
      const filtered = versions.filter(v => v.model_uuid === value);

      dispatch({
        type: 'UPDATE_BOARD',
        payload: { version_uuid: '' }
      });

      dispatch({
        type: 'SET_MODEL_UUID',
        payload: value
      });

      dispatch({
        type: 'SET_FILTERED_VERSION_LIST',
        payload: filtered
      });
    }
  }, [state.ki_plans, state.tenants, versions]);

  const onChangeKiSubscriptionState = useCallback((stateVal) => {
    const payload = { ki_subscription_state: stateVal };
    if (stateVal === STATE_NOT_APPLICABLE) {
      payload.renewal_interval = 0;
    }
    dispatch({
      type: 'UPDATE_PLAN_SUBSCRIPTION',
      payload
    });
  }, []);

  const onChangePlanDate = useCallback((dateString, field) => {
    const payload = { [field]: dateString ? dateString : null };
    if (field === 'ki_connection_date' && !state.plan_subscription.renewal_date) {
      payload.renewal_date = dateString ? dateString : null;
    }
    dispatch({
      type: 'UPDATE_PLAN_SUBSCRIPTION',
      payload
    });
  }, [state.plan_subscription.renewal_date]);

  const onChangePlanRenewalInterval = useCallback((e) => {
    const val = parseInt(e.target.value) || 0;
    dispatch({
      type: 'UPDATE_PLAN_SUBSCRIPTION',
      payload: { renewal_interval: val }
    });
  }, []);

  const handleSearchWorkOrder = useCallback((v) => {
    const list = [...state.work_orders];
    if (state.work_orders_original_length !== list.length) {
      list.pop();
    }
    if (!list.find(e => e.value.toString().indexOf(v) >= 0) && v) {
      list.push({ label: v, value: v });
    }
    dispatch({
      type: 'SET_WORK_ORDERS',
      payload: list
    });
  }, [state.work_orders, state.work_orders_original_length]);

  const handleChangeWorkOrder = useCallback((v) => {
    const woError = lengthValidation(v, 8) && typeValidation(v, 'number') ? '' : lang.handle('¡Orden de trabajo erronea!');
    dispatch({
      type: 'UPDATE_ERRORS',
      payload: { wo: woError }
    });

    const found = state.work_orders.find(e => e.value === v);
    const updatedBoard = { work_order: v };
    if (!state.board.serial_number) {
      updatedBoard.serial_number = found?.serialNumber ?? '';
    }
    dispatch({
      type: 'UPDATE_BOARD',
      payload: updatedBoard
    });
  }, [state.work_orders, state.board.serial_number, lang]);

  /**
   * Copia un valor al portapapeles y muestra una notificación de éxito o error.
   */
  const copyToClipboard = useCallback((value, fieldName) => {
    navigator.clipboard.writeText(value).then(() => {
      notification.success({
        message: `${lang.handle(fieldName)} ${lang.handle('copiado al portapapeles')}`,
      });
    }).catch(err => {
      console.error(err);
      notification.error({
        message: `${lang.handle('Error al copiar')}: ${lang.handle(fieldName)}`,
      });
    });
  }, [lang]);

  /**
   * Coloca el valor seleccionado del router en su estado correspondiente. 
   * Si se desasocia el router, se reinicia la suscripción M2M del box.
   */
  const onChangeRouter = useCallback((value) => {
    dispatch({ type: 'UPDATE_BOARD', payload: { router_uuid: value ?? '' } });

    // Solo si la M2M cargada era la del router.
    // Si el box llego sin router, la M2M es suya y se conserva.
    if (!value && loadedRouterUuid.current) {
      dispatch({
        type: 'UPDATE_M2M_SUBSCRIPTION',
        payload: { icc: '', state: '', hiring_date: null, renewal_date: null, renewal_interval: 0 },
      });
    }
  }, []);

  /**
   * Al escribir en Kite una SIM de Kiconex, la suscripcion pasa a lista para activacion
   */
  const handleKiteSaved = useCallback(() => {
    if (state.board.tenant_uuid === KICONEX_UUID &&
        state.m2m_subscription.state !== STATE_ACTIVE &&
        state.m2m_subscription.state !== STATE_CANCELLED) {
      dispatch({ type: 'UPDATE_M2M_SUBSCRIPTION', payload: { state: STATE_INACTIVE } });
    }
  }, [state.board.tenant_uuid, state.m2m_subscription.state]);

  /**
   * Validate
   */
  const validate = useCallback(() => {
    const { board: b, plan_subscription: ps, m2m_subscription: ms, errors: errs } = state;

    if (!b.order_id || b.order_id === "") {
      notification["error"]({ message: lang.handle('Pedido es un campo obligatorio') });
      return false;
    }
    if (!b.uuid || b.uuid === "") {
      notification["error"]({ message: lang.handle('UUID es un campo obligatorio') });
      return false;
    }
    if (!b.version_uuid || b.version_uuid === "") {
      notification["error"]({ message: lang.handle('Modelo y versión son campos obligatorios') });
      return false;
    }

    if (errs.uuid || errs.physical_id || errs.sn || errs.wo || errs.icc) {
      notification["error"]({ message: lang.handle('Rellene el formulario correctamente') });
      return false;
    }

    if (ms.icc && !iccValidation(ms.icc)) {
      notification["error"]({ message: lang.handle('¡ICC erróneo!') });
      return false;
    }

    if (!ps.ki_subscription_uuid || ps.ki_subscription_uuid === "") {
      notification["error"]({ message: lang.handle('Debe seleccionar una suscripción de plan') });
      return false;
    }
    if (!ps.ki_subscription_state || ps.ki_subscription_state === "") {
      notification["error"]({ message: lang.handle('Debe seleccionar un estado de suscripción') });
      return false;
    }
    if (
      [STATE_INACTIVE, STATE_ACTIVE].includes(ps.ki_subscription_state) && 
      (!ps.renewal_interval || ps.renewal_interval === "0" || ps.renewal_interval === 0)
    ) {
      notification["error"]({
        message: lang.handle('Estado de suscripción no compatible con el intervalo seleccionado'),
      });
      return false;
    }
    if (
      [STATE_INACTIVE, STATE_ACTIVE, STATE_CANCELLED].includes(ps.ki_subscription_state) && 
      (!ps.ki_subscription_name || ps.ki_subscription_name === "")
    ) {
      notification["error"]({
        message: lang.handle('Plan es obligatorio'),
      });
      return false;
    }

    if (!ms.state || ms.state === "") {
      notification["error"]({ message: lang.handle('Debe seleccionar un estado de suscripción M2M') });
      return false;
    }
    if (
      [STATE_INACTIVE, STATE_ACTIVE].includes(ms.state) && 
      (!ms.renewal_interval || ms.renewal_interval === "0" || ms.renewal_interval === 0)
    ) {
      notification["error"]({
        message: lang.handle('Estado de suscripción no compatible con el intervalo seleccionado'),
      });
      return false;
    }
    if (
      [STATE_INACTIVE, STATE_ACTIVE, STATE_CANCELLED].includes(ms.state) && 
      (!ms.icc || ms.icc === "")
    ) {
      notification["error"]({
        message: lang.handle('ICC es obligatorio'),
      });
      return false;
    }
    return true;
  }, [state, lang]);

  /**
   * Save
   */
  const onSaveData = useCallback(() => {
    const urlListaPlacas = "/boards/list";
    const urlPlacasManufactured = "/boards/manufacture/list";
    const urlPlacasAssigned = "/boards/manufacture/assigned";
    const stateVal = state.board.state;
    switch (stateVal) {
      case "Fabricado":
        navigate(urlPlacasManufactured);
        break;
      case "Asignado":
        navigate(urlPlacasAssigned);
        break;
      case "Terminado":
      default:
        navigate(urlListaPlacas);
        break;
    }
  }, [state.board.state, navigate]);

  /**
   * Submit
   */
  const onSubmit = useCallback(async (e) => {
    e.preventDefault();

    const valid = validate();
    if (!valid) {
      return;
    }

    const payload = {
      board: state.board,
      plan_subscription: state.plan_subscription,
      m2m_subscription: state.m2m_subscription
    };

    if (layer === 'assign' && !identProp?.id_stock) {
      payload.action = 'assign';
    } else {
      payload.action = 'updateWithTransaction';
    }

    try {
      const res = await service.put(`boards/${state.board.ki_id}`, payload);
      if (res && res.data.ok === false) {
        notification.error({ message: lang.handle('Error en la operación') });
        return;
      }

      notification.success({ 
        message: lang.handle(layer === 'assign' ? 'Placa creada' : 'Placa actualizada')
      });

      storeDispatch(invalidateRoutersLookup());

      onSaveData();
    } catch (err) {
      console.error('Error al realizar la operación del submit:', err);
      notification.error({ message: lang.handle('Error en la operación') });
    }
  }, [state.board, state.plan_subscription, state.m2m_subscription, layer, identProp, validate, onSaveData, lang, storeDispatch]);

  // ===== 7. EFFECTS =====
  /**
   * 1. Carga estática inicial de datos (ejecutada una única vez al montar) 
   */ 
  useEffect(() => {
    fetchStaticInitialData();
  }, [fetchStaticInitialData]);

  /**
   * 2. Carga de datos de la placa (depende de identProp y otros identificadores)
   */
  useEffect(() => {
    fetchBoardData();
  }, [fetchBoardData]);

  /**
   * 3. Resolución reactiva del modelo y la versión de la placa
   */
  useEffect(() => {
    if (!state.board.version_uuid) return;
    const version = versions.find(v => v.uuid === state.board.version_uuid);
    const model_uuid = version ? version.model_uuid : '';
    const filtered = model_uuid
      ? versions.filter(v => v.model_uuid === model_uuid)
      : [];
    dispatch({ type: 'SET_MODEL_UUID', payload: model_uuid });
    dispatch({ type: 'SET_FILTERED_VERSION_LIST', payload: filtered });
  }, [state.board.version_uuid, versions]);

  /**
   * 4. Resolución reactiva del nombre legible del tenant
   */
  useEffect(() => {
    if (state.tenants.length > 0 && state.board.tenant_uuid) {
      const tenant = state.tenants.find(e => e.uuid === state.board.tenant_uuid);
      if (tenant) {
        dispatch({
          type: 'SET_TENANT_NAME',
          payload: tenant.name
        });
      }
    }
  }, [state.tenants, state.board.tenant_uuid]);

  /**
   * 5. Actualización de la lista de tenants en el estado global
   */
  useEffect(() => {
    dispatch({ type: 'SET_TENANTS', payload: tenants });
  }, [tenants, dispatch]);

  // ===== 8. COLUMNAS =====
  const kiconexTab = useMemo(() => ({
    label: lang.handle('General'),
    key: '1',
    children: (
      <div>
        {/* Numero de pedido */}
        <div className="form-group row">
          <label htmlFor="order_id" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Pedido')} </label>
          <div className="col-sm-10 d-flex gap-2">
            <input
              type="text"
              className="form-control"
              id="order_id"
              placeholder="20PED4596"
              onChange={onInputChange}
              name="order_id"
              value={state.board.order_id}
              disabled={!kiconex_editable}
              required
            />
            <Button 
              onClick={() => copyToClipboard(state.board.order_id, 'Pedido')} 
              disabled={!state.board.order_id}
            >
              <CopyOutlined />
            </Button>
          </div>
        </div>

        {/* Tenant */}
        <div className="form-group row">
          <div className="col-sm-2 newCard-form-text d-flex align-items-center"> {lang.handle('Organización')} </div>
          <div className="col-sm-4">
            <Select
              disabled={spare || !kiconex_editable}
              showSearch
              allowClear
              onClear={clearTenant}
              className="form-control"
              optionFilterProp="tenant_uuid"
              value={state.tenant_name}
              onSelect={(value, event) => onChangeSelect(value, event)}
              filterOption={(input, option) =>
                String(option.value).toLowerCase().indexOf(String(input).toLowerCase()) >= 0
              }
              name="tenant_uuid"
              dropdownRender={options => (
                <div>
                  {options}
                  <Divider style={{ margin: '4px 0' }} />
                </div>
              )}
            >
              {state.tenants.map((tenant) => {
                return (
                  <Option key={tenant.uuid} type="tenant_name" value={tenant.name}>{tenant.name}</Option>
                )
              })}
            </Select>
          </div>
          
          {/* VPN */}  
          <div className="col-sm-1 newCard-form-text d-flex justify-content-end align-items-center"> {lang.handle('VPN')} </div>
          <div className="col-sm-5">
            <select
              className="form-control"
              onChange={onInputChange}
              name="vpn"
              value={state.board.vpn}
              disabled={!kiconex_editable}>
              <option value="0" defaultValue> {lang.handle('No')} </option>
              <option value="1"> {lang.handle('Sí')} </option>
            </select>
          </div>
        </div>

        {/* UUID placa */}
        <div className="form-group row">
          <label htmlFor="uuid" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('UUID')} </label>
          <div className="col-sm-10 d-flex gap-2">
            <input
              type="text"
              className="form-control"
              id="uuid"
              placeholder="37555f8a-1cab-486b-ab2e-1c65e80b2c12"
              onChange={onInputChange}
              name="uuid"
              value={state.board.uuid}
              disabled={spare || !kiconex_editable}
              required
            />
            <Button 
              onClick={() => copyToClipboard(state.board.uuid, 'UUID')} 
              disabled={!state.board.uuid}
            >
              <CopyOutlined />
            </Button>
            {state.errors.uuid.length > 0 && <span className='boardFormError'>{state.errors.uuid}</span>}
          </div>
        </div>

        {/* Nombre de instalacion */}
        <div className="form-group row">
          <label htmlFor="name" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Nombre instalación')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={state.board.name}
              disabled
            />
          </div>
        </div>

        {/* Nº serie */}
        <div className="form-group row">
          <label htmlFor="serial_number" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Nº serie')} </label>
          <div className="col-sm-10 d-flex gap-2">
            <input
              type="text"
              className="form-control"
              id="serial_number"
              placeholder="202005080005"
              onChange={onInputChange}
              name="serial_number"
              value={state.board.serial_number}
              disabled={!kiconex_editable}
            />
            <Button 
              onClick={() => copyToClipboard(state.board.serial_number, 'Número de serie')} 
              disabled={!state.board.serial_number}
            >
              <CopyOutlined />
            </Button>
            {state.errors.sn.length > 0 && <span className='boardFormError'>{state.errors.sn}</span>}
          </div>
        </div>

        {/* Orden de trabajo */}
        <div className="form-group row">
          <label htmlFor="work_order" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Orden de trabajo')} </label>
          <div className="col-sm-10">
            <Select
              className="form-control"
              onChange={handleChangeWorkOrder}
              id="work_order"
              name="work_order"
              value={state.board.work_order}
              disabled={!kiconex_editable}
              options={state.work_orders}
              showSearch
              onSearch={handleSearchWorkOrder}
              filterOption={(input, option) => {
                const strOption = option.label.toString();
                return strOption.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }}
            />
            {state.errors.wo.length > 0 && <span className='boardFormError'>{state.errors.wo}</span>}
          </div>
        </div>

        {/* Version hardware kiconex */}
        <div className="form-group row">
          <div className="col newCard-form-text d-flex align-items-center"> {lang.handle('Versión hardware')} </div>
          <div className="col-sm-10">
            <select
              className="form-control"
              onChange={onInputChange}
              name="hardware_version"
              value={state.board.hardware_version}
              disabled={!kiconex_editable}
              required>
              <option value="A20-OLinuXino-LIME2-e16Gs16M" defaultValue> A20-OLinuXino-LIME2-e16Gs16M </option>
              <option value="A20-OlinuXino-LIME2-e4GB"> A20-OlinuXino-LIME2-e4GB </option>
              <option value="A20-OLinuXino-LIME2-e4Gs16M"> A20-OLinuXino-LIME2-e4Gs16M </option> 
              <option value="Teltonika RUT200"> Teltonika RUT200 </option>
              <option value="Teltonika RUT230"> Teltonika RUT230 </option>
              <option value="Teltonika RUT906"> Teltonika RUT906 </option>
              <option value="Teltonika RUT955"> Teltonika RUT955 </option>
              <option value="Teltonika RUT956"> Teltonika RUT956 </option>
              <option value="Teltonika TRB145"> Teltonika TRB145 </option>
              <option value="Teltonika FTC305"> Teltonika FTC305 </option>
              <option value="Teltonika FMC650"> Teltonika FMC650 </option>
            </select>
          </div>
        </div>

        {/* physical_id */}
        <div className="form-group row">
          <label htmlFor="physical_id" className="col-sm-2 col-form-label newCard-form-text"> MAC / IMEI </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="physical_id"
              placeholder="30:A4:F5:20:4C:5A"
              onChange={onInputChange}
              name="physical_id"
              value={state.board.physical_id}
              disabled={!kiconex_editable || !!state.modem.imei}
            />
            {state.errors.physical_id.length > 0 && <span className='boardFormError'>{state.errors.physical_id}</span>}
          </div>
        </div>

        {/* Fecha Placa */}
        <div className="form-group row">
          <div className="col newCard-form-text d-flex align-items-center"> {lang.handle('Fecha fabricación')} </div>
          <div className="col-sm-10">
            <DatePicker
              className="form-control"
              value={dayjs(state.board.date, "DD-MM-YYYY")}
              onChange={onChangeDate}
              format="DD-MM-YYYY"
              disabled={!kiconex_editable}
              required />
          </div>
        </div>

        <div className="form-group row">
          {/* Modelo de la placa */}
          <div className="col-sm-2 newCard-form-text d-flex align-items-center"> {lang.handle('Modelo placa')} </div>
          <div className="col-sm-4">
            <select
              className="form-control"
              onChange={(e) => onChangeSelect(e.target.value, { type: 'model_name' })}
              name="model_uuid"
              value={state.model_uuid}
              disabled={!kiconex_editable}
              required>
              <option value=""> --{lang.handle('Seleccionar opción')}-- </option>
              {models
                .filter(m => m.device_type === 'kibox')
                .map((model) => (
                  <option key={model.uuid} value={model.uuid}>
                    {model.name}
                  </option>
              ))}
            </select>
          </div>

          {/* Versión placa */}
          <div className="col-sm-1 newCard-form-text text-end d-flex justify-content-end align-items-center"> {lang.handle('Versión')} </div>
          <div className="col-sm-5">
            <select
              className="form-control"
              onChange={onInputChange}
              name="version_uuid"
              value={state.board.version_uuid}
              disabled={!kiconex_editable || !state.model_uuid}
              required
            >
              <option value="" > 
                {state.model_uuid 
                  ? lang.handle('Seleccionar opción')
                  : lang.handle('Primero seleccione un modelo')}
              </option>
              {state.filtered_version_list.map((version) => (
                <option key={version.uuid} value={version.uuid}> 
                  {version.name} 
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Router kiconex */}
        <div className="form-group row">
          <label htmlFor="router_uuid" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Router kiconex')} </label>
          <div className="col-sm-10">
            <Select
              className="form-control"
              id="router_uuid"
              placeholder={lang.handle('Seleccionar router')}
              value={state.board.router_uuid || undefined}
              onChange={onChangeRouter}
              disabled={!kiconex_editable}
              options={routerOptions}
              showSearch
              allowClear
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            />
          </div>
        </div>

        {/* Valor antiguo en texto libre, hasta que se migre en C6 */}
        {state.board.ki_router_sn && (
          <div className="form-group row">
            <label className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Router (dato antiguo)')} </label>
            <div className="col-sm-10">
              <input type="text" className="form-control" value={state.board.ki_router_sn} disabled />
            </div>
          </div>
        )}

        {/* Notas */}
        <div className="form-group row">
          <label htmlFor="notes" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Notas')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="notes"
              placeholder={lang.handle('Este pedido debe salir con una pegatina extra')}
              onChange={onInputChange}
              name="notes"
              value={state.board.notes}
              disabled={!kiconex_editable} />
          </div>
        </div>

        {/* Estado de fabricación */}
        <div className="form-group row">
          <div className="col-sm-2 newCard-form-text d-flex align-items-center"> {lang.handle('Estado de fabricación')} </div>
          <div className="col-sm-10">
            <select
              className="form-control"
              onChange={onInputChange}
              name="state"
              value={state.board.state}
              disabled={!kiconex_editable}
              required>
              <option value="Fabricado"> {lang.handle('Fabricado')} </option>
              <option value="Asignado" defaultValue> {lang.handle('Asignado')} </option>
              <option value="Terminado"> {lang.handle('Terminado')} </option>
              <option value="Eliminado"> {lang.handle('Eliminado')} </option>
            </select>
          </div>
        </div>
      </div>
    )
  }), [lang, onInputChange, state.board, kiconex_editable, spare, clearTenant, onChangeSelect, state.tenants, state.tenant_name, state.errors, handleChangeWorkOrder, state.work_orders, handleSearchWorkOrder, onChangeDate, state.model_uuid, state.filtered_version_list, copyToClipboard, routerOptions, onChangeRouter, models, state.modem.imei]);

  const comercialTab = useMemo(() => ({
    label: lang.handle('Comercial'),
    key: '2',
    children: (
      <div>
        {/* Nombre de placa */}
        <div className="form-group row">
          <label htmlFor="name" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Nombre')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="comercial_name"
              onChange={onInputChange}
              name="comercial_info.name"
              value={state.board.comercial_info.name}
              disabled={!editable}
            />
          </div>
        </div>

        {/* Cliente final */}
        <div className="form-group row">
          <label htmlFor="final_client" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Cliente final')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="final_client"
              onChange={onInputChange}
              name="final_client"
              value={state.board.final_client}
              disabled={!editable}
            />
          </div>
        </div>

        <Divider />

        {/* Numero de pedido comercial */}
        <div className="form-group row">
          <label htmlFor="comercial_order_id" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Pedido')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="comercial_order_id"
              onChange={onInputChange}
              name="comercial_info.order_id"
              value={state.board.comercial_info.order_id}
              disabled={!editable}
            />
          </div>
        </div>

        {/* Numero de serie comercial */}
        <div className="form-group row">
          <label htmlFor="comercial_serial_number" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Número de serie')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="comercial_serial_number"
              onChange={onInputChange}
              name="comercial_info.serial_number"
              value={state.board.comercial_info.serial_number}
              disabled={!editable}
            />
          </div>
        </div>

        {/* Modelo de equipo comercial */}
        <div className="form-group row">
          <label htmlFor="comercial_model" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Modelo del equipo')} </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="comercial_model"
              onChange={onInputChange}
              name="comercial_info.model"
              value={state.board.comercial_info.model}
              disabled={!editable}
            />
          </div>
        </div>

        {/* Estado comercial */}
        <div className="form-group row">
          <div className="col-sm-2 newCard-form-text"> {lang.handle('Estado')} </div>
          <div className="col-sm-10">
            <select
              className="form-control"
              onChange={onInputChange}
              name="comercial_info.state"
              value={state.board.comercial_info.state}
              disabled={!editable}
              required>
              <option value="Stock" defaultValue> {lang.handle('Stock')} </option>
              <option value="Asignado"> {lang.handle('Asignado')} </option>
              <option value="Terminado"> {lang.handle('Terminado')} </option>
            </select>
          </div>
        </div>
      </div>
    )
  }), [lang, onInputChange, state.board.comercial_info, state.board.final_client, editable]);

  const subscriptionsTab = useMemo(() => ({
    label: lang.handle('Subscripción'),
    key: '3',
    children: (
      <div>
         {/* Plan de plataforma kiconex */}
        <div className="form-group row">
          <div className="col newCard-form-text d-flex align-items-center"> {lang.handle('Plan kiconex')} </div>
          <div className="col-sm-10">
            <Select
              disabled={spare || !kiconex_editable}
              showSearch
              className="form-control"
              optionFilterProp="ki_subscription_name"
              value={state.plan_subscription.ki_subscription_name}
              placeholder="ki8PRO"
              onSelect={(value, event) => onChangeSelect(value, event)}
              filterOption={(input, option) =>
                option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              name="ki_subscription_name"
              dropdownRender={options => (
                <div>
                  {options}
                  <Divider style={{ margin: '4px 0' }} />
                </div>
              )}
            >
              {state.ki_plans.name.map(subscription => (
                <Option type="ki_subscription_name" key={subscription}> {subscription} </Option>
              ))}
            </Select>
          </div>
        </div>
        {/* Estado del plan kiconex */}
        <div className="form-group row">
          <label htmlFor="name" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Estado del plan')} </label>
          <div className="col-sm-10">
            <Select
              className="form-control"
              onChange={onChangeKiSubscriptionState}
              name="ki_subscription_state"
              value={state.plan_subscription.ki_subscription_state || ''}
              disabled={spare || !kiconex_editable}
              options={optionsState}
            />
          </div>
        </div>
        {/* Fecha primera conexión del plan kiconex */}
        <div className="form-group row">
          <label htmlFor="name" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Fecha primera conexión')} </label>
          <div className="col-sm-10">
            <DatePicker
              className="form-control"
              value={state.plan_subscription.ki_connection_date ? dayjs(state.plan_subscription.ki_connection_date, "DD-MM-YYYY") : null}
              onChange={(date, dateString) => onChangePlanDate(dateString, 'ki_connection_date')}
              format="DD-MM-YYYY"
              disabled={spare || !kiconex_editable || planCancelledOrNotApplicable}
            />
          </div>
        </div>
        <div className="form-group row">
          {/* Fecha última renovación del plan kiconex */}
          <label htmlFor="name" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Fecha última renovación')} </label>
          <div className="col-sm-10">
            <DatePicker
              className="form-control"
              value={state.plan_subscription.renewal_date ? dayjs(state.plan_subscription.renewal_date, "DD-MM-YYYY") : null}
              onChange={(date, dateString) => onChangePlanDate(dateString, 'renewal_date')}
              format="DD-MM-YYYY"
              disabled={spare || !kiconex_editable || planCancelledOrNotApplicable}
            />
          </div>
        </div>
        {/* Intervalo de renovación del plan kiconex */}
        <div className="form-group row">
          <label htmlFor="renewal_interval" className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Intervalo de renovación')} </label>
          <div className="col-sm-10">
            <input
              type="number"
              className="form-control"
              id="renewal_interval"
              name="renewal_interval"
              min="0"
              max="120"
              step="1"
              value={state.plan_subscription.renewal_interval}
              onChange={onChangePlanRenewalInterval}
              disabled={spare || !kiconex_editable || planCancelledOrNotApplicable}
            />
            <small className="form-text text-muted">{lang.handle('Ingrese el número de meses')}</small>
          </div>
        </div>

        <Divider />

        <h3>M2M</h3>
        <M2MSubscriptionFields
          value={state.m2m_subscription}
          onChange={(patch) => dispatch({ type: 'UPDATE_M2M_SUBSCRIPTION', payload: patch })}
          editable={!spare && kiconex_editable}
          suggestedOwner={state.tenant_name}
          suggestedAlias={m2mSuggestedAlias}
          defaultSimType={m2mDefaultSimType}
          onKiteSaved={handleKiteSaved}
        />
      </div>
    )
  }), [lang, onChangeKiSubscriptionState, state.plan_subscription, spare, kiconex_editable, onChangePlanDate, planCancelledOrNotApplicable, onChangePlanRenewalInterval, state.m2m_subscription, state.ki_plans, onChangeSelect, state.tenant_name, m2mSuggestedAlias, m2mDefaultSimType, handleKiteSaved]);
  
  const modemTab = useMemo(() => ({
    label: lang.handle('Modem'),
    key: '4',
    children: <ModemInfo data={state.modem} />
  }), [lang, state.modem]);

  const routerTab = useMemo(() => ({
    label: lang.handle('Router'),
    key: '5',
    children: (
      <RouterInfo
        uuid={state.board.router_uuid}
        order_id={state.board.order_id}
      />
    )
  }), [lang, state.board.router_uuid, state.board.order_id]);

  const items = useMemo(() => {
    const tabs = [kiconexTab, comercialTab, subscriptionsTab];
    // La pestaña solo existe si la placa lleva Teltonika: Box2 y Box4 no llevan
    if (state.modem.imei) tabs.push(modemTab);
    // Y la del router solo si tiene uno asignado
    if (state.board.router_uuid) tabs.push(routerTab);
    return tabs;
  }, [kiconexTab, comercialTab, subscriptionsTab, modemTab, routerTab, state.modem.imei, state.board.router_uuid]);

  // ===== 9. RENDER =====
  return (
    <div className="bg-white card">
      {state.loading && <Loading loading={state.loading} />}
      {/* Titulo de la tarjeta */}
      <h4 className="card-title p-3"> {title} </h4>

      {/* Formulario con los datos a cumplimentar */}
      <form 
        onSubmit={onSubmit} 
        className="p-3" 
        noValidate
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
            e.preventDefault();
          }
        }}
      >
        <Tabs
          type="card"
          items={items}
        />
        {editable && (
          <div className="buttonSave">
            <Space>
              <Button
                id="button-save"
                htmlType="submit"
                type="primary"
                icon={<SaveOutlined/>}
              >
                {lang.handle('Guardar')}
              </Button>
            </Space>
          </div>
        )}
      </form>
    </div>
  );
}

export default withLocale(withNavigation(BoardCard));
